"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { attachPrincepsToGenericFamilies, classifyLgpiProduct } from "./lib/lgpi-catalog";

type Pharmacy = "Square" | "Ragadan" | "Laurentine";

type CatalogItem = {
  id: number;
  pharmacy: Pharmacy;
  ean: string;
  product: string;
  stock: number;
  daysToSell: number;
  lastSaleDate: string;
  lastReceptionDate: string;
  rotationFamily: string;
  genericRole: string;
  vatRate: number;
  reimbursementRate: number;
  productType: string;
  monthlySales: number;
  importedAt: number;
};

type TransferChoice = {
  destination: Pharmacy;
  item: CatalogItem;
};

type CatalogStat = {
  pharmacy: Pharmacy;
  productCount: number;
  lastImport: number;
};

type RawCatalogItem = Record<string, unknown>;

type Props = {
  onBack: () => void;
  onOpenClearance: () => void;
  initialPharmacy: Pharmacy;
  initialOperator: string;
};

const pharmacies: Pharmacy[] = ["Square", "Ragadan", "Laurentine"];
const productTypes = ["PHN / conseil", "Remboursé", "Générique", "Princeps", "Autre"];

const sampleImport = `EAN;Dénomination;Stock;Jours d'écoulement;Dernière vente;Dernière réception;Famille de rotation;Rôle générique;TVA;Taux remboursement;Type;Ventes mensuelles
3400930000000;Produit exemple;12;185;15/01/2026;01/10/2025;OMEPRAZOLE 40 MG 28;Générique;10;65;Remboursé;0,4`;

const numberValue = (value: unknown) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

function normalizeItem(row: RawCatalogItem): CatalogItem {
  return {
    id: numberValue(row.id),
    pharmacy: String(row.pharmacy || "Square") as Pharmacy,
    ean: String(row.ean || ""),
    product: String(row.display_product ?? row.product ?? row.ean ?? ""),
    stock: numberValue(row.stock),
    daysToSell: numberValue(row.days_to_sell ?? row.daysToSell),
    lastSaleDate: String(row.last_sale_date ?? row.lastSaleDate ?? ""),
    lastReceptionDate: String(row.last_reception_date ?? row.lastReceptionDate ?? ""),
    rotationFamily: String(row.rotation_family ?? row.rotationFamily ?? ""),
    genericRole: String(row.generic_role ?? row.genericRole ?? ""),
    vatRate: numberValue(row.vat_rate ?? row.vatRate),
    reimbursementRate: numberValue(row.reimbursement_rate ?? row.reimbursementRate),
    productType: String(row.product_type ?? row.productType ?? "Autre"),
    monthlySales: numberValue(row.monthly_sales ?? row.monthlySales),
    importedAt: numberValue(row.imported_at ?? row.importedAt),
  };
}

function normalizeStat(row: RawCatalogItem): CatalogStat {
  return {
    pharmacy: String(row.pharmacy || "Square") as Pharmacy,
    productCount: numberValue(row.product_count ?? row.productCount),
    lastImport: numberValue(row.last_import ?? row.lastImport),
  };
}

function formatDate(date: string, empty = "Jamais vendue") {
  if (!date) return empty;
  const [year, month, day] = date.split("-");
  return `${day}/${month}/${year}`;
}

function formatImportDate(timestamp: number) {
  if (!timestamp) return "Aucun import";
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

function detectSeparator(line: string) {
  const candidates = ["\t", ";", "|"];
  return candidates.sort((a, b) => line.split(b).length - line.split(a).length)[0];
}

function normalizeHeader(value: string) {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLocaleLowerCase("fr")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function parseCataloguePaste(value: string) {
  const lines = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const separator = detectSeparator(lines[0]);
  const headers = lines[0].split(separator).map(normalizeHeader);
  const find = (...tokens: string[]) => headers.findIndex((header) => tokens.some((token) => header.includes(token)));
  const indexes = {
    ean: find("ean", "cip", "code produit"),
    product: find("denomination", "designation", "produit", "libelle"),
    stock: find("stock"),
    daysToSell: find("jours d ecoulement", "jour ecoulement", "jours ecoul"),
    lastSaleDate: find("derniere vente", "date derniere vente"),
    lastReceptionDate: find("derniere reception", "date derniere reception", "date dern recept", "derniere entree"),
    rotationFamily: find("famille de rotation", "famille rotation", "famille substituable", "famille generique", "groupe generique", "classification generique"),
    genericRole: find("role generique", "statut generique", "type generique", "princeps referent"),
    vatRate: find("tva"),
    reimbursementRate: find("remboursement", "taux remb"),
    productType: find("typologie", "type produit", "type"),
    prestation: find("prestation"),
    monthlySales: find("ventes mensuelles", "moyenne mensuelle", "moy mens"),
  };
  if (indexes.ean < 0 || indexes.product < 0) return [];
  const cell = (cells: string[], index: number) => index >= 0 ? cells[index]?.trim() ?? "" : "";
  const number = (input: string) => Number(input.replace(/\s/g, "").replace(",", ".").replace("%", "")) || 0;

  const rows = lines.slice(1).map((line) => {
    const cells = line.split(separator);
    const prestationIndex = indexes.prestation >= 0 ? indexes.prestation : cells.length >= 4 ? 3 : -1;
    const genericIndex = indexes.rotationFamily;
    const prestation = cell(cells, prestationIndex);
    const genericMarker = cell(cells, genericIndex);
    const product = cell(cells, indexes.product);
    const classification = classifyLgpiProduct({
      product,
      prestation,
      genericMarker,
      explicitFamily: cell(cells, indexes.rotationFamily),
      explicitRole: cell(cells, indexes.genericRole),
      explicitType: cell(cells, indexes.productType),
    });
    return {
      ean: cell(cells, indexes.ean).replace(/\D/g, ""),
      product,
      stock: number(cell(cells, indexes.stock)),
      daysToSell: number(cell(cells, indexes.daysToSell)),
      lastSaleDate: cell(cells, indexes.lastSaleDate),
      lastReceptionDate: cell(cells, indexes.lastReceptionDate),
      rotationFamily: classification.rotationFamily,
      genericRole: classification.genericRole,
      vatRate: number(cell(cells, indexes.vatRate)),
      reimbursementRate: number(cell(cells, indexes.reimbursementRate)),
      productType: classification.productType,
      monthlySales: number(cell(cells, indexes.monthlySales)),
    };
  }).filter((row) => row.ean.length >= 8 && row.product);
  return attachPrincepsToGenericFamilies(rows);
}

export function DormantProductsModule({
  onBack,
  onOpenClearance,
  initialPharmacy,
  initialOperator,
}: Props) {
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [comparisonItems, setComparisonItems] = useState<CatalogItem[]>([]);
  const [stats, setStats] = useState<CatalogStat[]>([]);
  const [pharmacy, setPharmacy] = useState<Pharmacy>(initialPharmacy);
  const [query, setQuery] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [minimumStock, setMinimumStock] = useState(1);
  const [daysThreshold, setDaysThreshold] = useState(90);
  const [lastSaleThreshold, setLastSaleThreshold] = useState(90);
  const [receptionThreshold, setReceptionThreshold] = useState(30);
  const [daysFilterEnabled, setDaysFilterEnabled] = useState(true);
  const [lastSaleFilterEnabled, setLastSaleFilterEnabled] = useState(true);
  const [sort, setSort] = useState<"days" | "stock" | "last_sale" | "name">("days");
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importPharmacy, setImportPharmacy] = useState<Pharmacy>(initialPharmacy);
  const [replaceCatalogue, setReplaceCatalogue] = useState(true);
  const [importing, setImporting] = useState(false);
  const [sendingEan, setSendingEan] = useState("");
  const [detailsEan, setDetailsEan] = useState("");
  const [matchedCount, setMatchedCount] = useState(0);
  const [matchedStock, setMatchedStock] = useState(0);
  const [transferChoices, setTransferChoices] = useState<Record<string, TransferChoice>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cutoff = new Date(Date.now() - lastSaleThreshold * 86_400_000).toISOString().slice(0, 10);
      const receptionCutoff = new Date(Date.now() - receptionThreshold * 86_400_000).toISOString().slice(0, 10);
      const params = new URLSearchParams({
        mode: "dormant",
        pharmacy,
        minimumStock: String(minimumStock),
        daysThreshold: daysFilterEnabled ? String(daysThreshold) : "0",
        lastSaleBefore: lastSaleFilterEnabled ? cutoff : "",
        lastReceptionBefore: receptionCutoff,
        filterJoin: "and",
        productTypes: selectedTypes.join("|"),
        sort,
        search: query.trim(),
        limit: "800",
      });
      const response = await fetch(`/api/product-catalog?${params}`, { cache: "no-store" });
      const data = await response.json() as {
        items?: RawCatalogItem[];
        comparisonItems?: RawCatalogItem[];
        stats?: RawCatalogItem[];
        matchedCount?: number;
        matchedStock?: number;
        error?: string;
      };
      if (!response.ok) throw new Error(data.error);
      setItems((data.items ?? []).map(normalizeItem));
      setComparisonItems((data.comparisonItems ?? []).map(normalizeItem));
      setStats((data.stats ?? []).map(normalizeStat));
      setMatchedCount(Number(data.matchedCount ?? 0));
      setMatchedStock(Number(data.matchedStock ?? 0));
    } catch {
      setNotice("Le catalogue n’a pas pu être chargé.");
    } finally {
      setLoading(false);
    }
  }, [daysFilterEnabled, daysThreshold, lastSaleFilterEnabled, lastSaleThreshold, minimumStock, pharmacy, query, receptionThreshold, selectedTypes, sort]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 180);
    return () => window.clearTimeout(timer);
  }, [load]);

  const selectedItems = useMemo(() => {
    return items;
  }, [items]);

  const comparisons = useMemo(() => {
    const map = new Map<string, Partial<Record<Pharmacy, CatalogItem>>>();
    [...items, ...comparisonItems].forEach((item) => {
      const entry = map.get(item.ean) ?? {};
      entry[item.pharmacy] = item;
      map.set(item.ean, entry);
    });
    return map;
  }, [comparisonItems, items]);

  const selectedTransfers = useMemo(() => Object.values(transferChoices), [transferChoices]);
  const printRows = selectedTransfers.length
    ? selectedTransfers
    : selectedItems.map((item) => ({ destination: item.pharmacy, item }));

  const toggleTransfer = (item: CatalogItem, destination: Pharmacy) => {
    const key = `${destination}:${item.pharmacy}:${item.ean}`;
    setTransferChoices((current) => {
      const next = { ...current };
      if (next[key]) delete next[key];
      else next[key] = { item, destination };
      return next;
    });
  };

  const activeStat = stats.find((entry) => entry.pharmacy === pharmacy);
  const toggleType = (type: string) => {
    setSelectedTypes((current) => current.includes(type)
      ? current.filter((entry) => entry !== type)
      : [...current, type]);
  };

  const importCatalogue = async () => {
    const rows = parseCataloguePaste(importText);
    if (!rows.length) {
      setNotice("Je ne reconnais pas les colonnes. Gardez au minimum EAN et Dénomination.");
      return;
    }
    setImporting(true);
    try {
      const chunks = Array.from({ length: Math.ceil(rows.length / 500) }, (_, index) => rows.slice(index * 500, index * 500 + 500));
      let imported = 0;
      for (let index = 0; index < chunks.length; index += 1) {
        const response = await fetch("/api/product-catalog", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pharmacy: importPharmacy,
            rows: chunks[index],
            replacePharmacy: replaceCatalogue && index === 0,
            source: "Import portail",
          }),
        });
        const data = await response.json() as { imported?: number; error?: string };
        if (!response.ok) throw new Error(data.error);
        imported += data.imported ?? 0;
      }
      setNotice(`${imported.toLocaleString("fr-FR")} produit${imported > 1 ? "s" : ""} intégré${imported > 1 ? "s" : ""} pour ${importPharmacy}.`);
      setImportText("");
      setImportOpen(false);
      setPharmacy(importPharmacy);
      await load();
    } catch {
      setNotice("L’import n’a pas pu être terminé.");
    } finally {
      setImporting(false);
    }
  };

  const sendToClearance = async (item: CatalogItem) => {
    setSendingEan(item.ean);
    try {
      const response = await fetch("/api/clearance-products", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ean: item.ean,
          product: item.product,
          pharmacies: [item.pharmacy],
          stocks: { Square: 0, Ragadan: 0, Laurentine: 0, [item.pharmacy]: item.stock },
          slowMoving: true,
          maximumPriority: item.daysToSell >= 365,
          note: `Détecté dans les produits dormants · ${Math.round(item.daysToSell)} jours d’écoulement · dernière vente ${formatDate(item.lastSaleDate)}.`,
          triggers: [{
            id: `catalog-${item.pharmacy}-${item.ean}`,
            type: "ean",
            value: item.ean,
            instruction: `Penser à proposer ${item.product} si le besoin correspond.`,
            active: true,
          }],
          createdBy: initialOperator,
        }),
      });
      const data = await response.json() as { error?: string };
      if (!response.ok) throw new Error(data.error);
      setNotice(`${item.product} a été envoyé vers Produits à écouler.`);
    } catch {
      setNotice("Ce produit n’a pas pu être envoyé vers Produits à écouler.");
    } finally {
      setSendingEan("");
    }
  };

  return (
    <main className="dormant-app">
      <header className="module-header">
        <button type="button" className="module-back" onClick={onBack}>← <span>Retour à l’accueil</span></button>
        <div className="wordmark module-wordmark">
          <span className="wordmark-symbol"><i /><i /><i /></span>
          <span><strong>saint-laurent</strong><small>pharmacies</small></span>
        </div>
        <div className="module-header-actions">
          <a className="module-tools-shortcut" href="/?tools=1" title="Ouvrir les applications"><span>🧰</span><b>Applications</b></a>
          <span className="module-status"><i /> Catalogue partagé</span>
        </div>
      </header>

      <section className="dormant-page">
        <header className="dormant-hero">
          <div>
            <span className="dormant-kicker">📦 STOCK · ROTATION · MULTI-PHARMACIES</span>
            <h1>Produits dormants</h1>
            <p>Repérez immédiatement ce qui dort, voyez si une autre pharmacie le vend et transformez l’analyse en action comptoir.</p>
          </div>
          <div className="dormant-hero-actions">
            <button type="button" className="secondary" onClick={() => window.print()}>📄 Listing / PDF{selectedTransfers.length ? ` · ${selectedTransfers.length}` : ""}</button>
            <button type="button" className="secondary" onClick={() => void load()}>↻ Actualiser</button>
            <button type="button" className="primary" onClick={() => setImportOpen(true)}>＋ Importer un catalogue</button>
          </div>
        </header>

        <section className="dormant-summary">
          <article><span>Produits détectés</span><strong>{matchedCount.toLocaleString("fr-FR")}</strong><small>{matchedCount > selectedItems.length ? `${selectedItems.length} affichés en priorité` : "avec les filtres actuels"}</small></article>
          <article><span>Unités immobilisées</span><strong>{Math.round(matchedStock).toLocaleString("fr-FR")}</strong><small>stock total à examiner</small></article>
          <article><span>Catalogue {pharmacy}</span><strong>{(activeStat?.productCount ?? 0).toLocaleString("fr-FR")}</strong><small>{formatImportDate(activeStat?.lastImport ?? 0)}</small></article>
        </section>

        <section className="dormant-filters">
          <div className="dormant-pharmacy-switch" role="tablist" aria-label="Pharmacie analysée">
            {pharmacies.map((entry) => <button type="button" role="tab" aria-selected={pharmacy === entry} className={pharmacy === entry ? "active" : ""} key={entry} onClick={() => setPharmacy(entry)}>
              <span>{entry === "Square" ? "✚" : entry === "Ragadan" ? "🌿" : "LP"}</span>
              <b>{entry}</b>
              <small>{(stats.find((stat) => stat.pharmacy === entry)?.productCount ?? 0).toLocaleString("fr-FR")} références</small>
            </button>)}
          </div>

          <div className="dormant-filter-grid compact">
            <label className="dormant-search"><span>Rechercher</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Produit, EAN ou typologie…" /></label>
            <label><span>Stock minimum</span><input type="number" min="1" value={minimumStock} onChange={(event) => setMinimumStock(Math.max(1, Number(event.target.value) || 1))} /></label>
          </div>

          <div className="dormant-choice-filters">
            <fieldset className={daysFilterEnabled ? "filter-mode-active" : "filter-mode-muted"}>
              <legend><label><input type="checkbox" checked={daysFilterEnabled} onChange={(event) => setDaysFilterEnabled(event.target.checked)} /> Jours d’écoulement</label></legend>
              <label className="dormant-days-input"><input type="number" min="1" max="3650" disabled={!daysFilterEnabled} value={daysThreshold} onChange={(event) => setDaysThreshold(Math.max(1, Number(event.target.value) || 1))} /><span>jours minimum</span></label>
              {[90, 180, 365].map((value) => <button type="button" disabled={!daysFilterEnabled} className={daysThreshold === value ? "active" : ""} key={value} onClick={() => setDaysThreshold(value)}>{value === 365 ? "1 an" : `${value} j`}</button>)}
            </fieldset>
            <fieldset className={lastSaleFilterEnabled ? "filter-mode-active" : "filter-mode-muted"}>
              <legend><label><input type="checkbox" checked={lastSaleFilterEnabled} onChange={(event) => setLastSaleFilterEnabled(event.target.checked)} /> Dernière vente</label></legend>
              <label className="dormant-days-input"><input type="number" min="1" max="3650" disabled={!lastSaleFilterEnabled} value={lastSaleThreshold} onChange={(event) => setLastSaleThreshold(Math.max(1, Number(event.target.value) || 1))} /><span>jours sans vente</span></label>
              {[90, 180, 365].map((value) => <button type="button" disabled={!lastSaleFilterEnabled} className={lastSaleThreshold === value ? "active" : ""} key={value} onClick={() => setLastSaleThreshold(value)}>{value === 90 ? "3 mois" : value === 180 ? "6 mois" : "1 an"}</button>)}
            </fieldset>
            <fieldset className="filter-mode-active master-filter">
              <legend>📥 Âge minimum depuis la dernière réception</legend>
              <label className="dormant-days-input"><input type="number" min="1" max="3650" value={receptionThreshold} onChange={(event) => setReceptionThreshold(Math.max(1, Number(event.target.value) || 1))} /><span>jours minimum depuis réception</span></label>
              {[30, 60].map((value) => <button type="button" className={receptionThreshold === value ? "active" : ""} key={value} onClick={() => setReceptionThreshold(value)}>{value} j</button>)}
            </fieldset>
            <fieldset className="sort"><legend>Classer par</legend>{([
              ["days", "Rotation lente"],
              ["stock", "Stock élevé"],
              ["last_sale", "Vente ancienne"],
              ["name", "Nom"],
            ] as const).map(([value, label]) => <button type="button" className={sort === value ? "active" : ""} key={value} onClick={() => setSort(value)}><i>{sort === value ? "✓" : ""}</i>{label}</button>)}</fieldset>
          </div>

          <p className="dormant-filter-summary">
            <span>{daysFilterEnabled && lastSaleFilterEnabled ? "3 CRITÈRES ACTIFS" : "2 CRITÈRES ACTIFS"}</span>
            {daysFilterEnabled && lastSaleFilterEnabled
              ? `Afficher les produits qui dépassent ${daysThreshold} jours d’écoulement et dont la dernière vente remonte à au moins ${lastSaleThreshold} jours.`
              : daysFilterEnabled
                ? `Filtrer uniquement sur les produits qui dépassent ${daysThreshold} jours d’écoulement.`
                : lastSaleFilterEnabled
                  ? `Filtrer uniquement sur les produits dont la dernière vente remonte à au moins ${lastSaleThreshold} jours.`
              : "Activez au moins un des deux critères pour cibler les produits dormants."} {`Le résultat conserve uniquement les produits dont la dernière réception date d’au moins ${receptionThreshold} jours ; une réception plus récente est exclue.`}
          </p>

          <div className="dormant-type-filters">
            <span>Typologie</span>
            {productTypes.map((type) => <button type="button" className={selectedTypes.includes(type) ? "active" : ""} key={type} onClick={() => toggleType(type)}><i>{selectedTypes.includes(type) ? "✓" : ""}</i>{type}</button>)}
            {selectedTypes.length > 0 && <button type="button" className="clear" onClick={() => setSelectedTypes([])}>Tout afficher</button>}
          </div>
        </section>

        {selectedTransfers.length > 0 && <section className="dormant-transfer-tray">
          <div>
            <span>🚚 TRANSFERTS À PRÉPARER</span>
            <h2>{selectedTransfers.length} produit{selectedTransfers.length > 1 ? "s" : ""} sélectionné{selectedTransfers.length > 1 ? "s" : ""}</h2>
            <p>La liste est prête à être imprimée ou enregistrée en PDF, classée par pharmacie destinataire.</p>
          </div>
          <div className="dormant-transfer-tray-actions">
            <button type="button" onClick={() => window.print()}>📄 Sortir le listing</button>
            <button type="button" className="clear" onClick={() => setTransferChoices({})}>Vider</button>
          </div>
        </section>}

        <section className="dormant-results">
          <header>
            <div><span>ANALYSE {pharmacy.toLocaleUpperCase("fr")}</span><h2>Produits à examiner</h2></div>
            <b>{matchedCount.toLocaleString("fr-FR")} résultat{matchedCount > 1 ? "s" : ""}</b>
          </header>

          {loading ? <div className="dormant-empty"><span>⏳</span><h3>Analyse du catalogue…</h3></div> : selectedItems.map((item) => {
            const comparison = comparisons.get(item.ean) ?? {};
            const open = detailsEan === item.ean;
            return <article className={`dormant-product ${item.daysToSell >= 365 ? "critical" : ""}`} key={`${item.pharmacy}-${item.ean}`}>
              <div className="dormant-product-main">
                <div className="dormant-product-name">
                  <span>{item.daysToSell >= 365 ? "🔴 À PRIORISER" : item.daysToSell >= 180 ? "🟠 À SURVEILLER" : "🟢 À OPTIMISER"}</span>
                  <h3>{item.product}</h3>
                  <p>EAN {item.ean}</p>
                  <button type="button" onClick={() => setDetailsEan(open ? "" : item.ean)}>{open ? "Masquer les détails ↑" : "Voir les détails ↓"}</button>
                </div>
                <div className="dormant-metrics">
                  <span><small>Stock</small><strong>{item.stock.toLocaleString("fr-FR")}</strong></span>
                  <span><small>Jours d’écoulement</small><strong>{Math.round(item.daysToSell).toLocaleString("fr-FR")} j</strong></span>
                  <span><small>Ventes / mois</small><strong>{item.monthlySales.toLocaleString("fr-FR", { maximumFractionDigits: 2 })}</strong></span>
                  <span><small>Dernière vente</small><strong>{formatDate(item.lastSaleDate)}</strong></span>
                  <span><small>Dernière réception</small><strong>{formatDate(item.lastReceptionDate, "Non renseignée")}</strong></span>
                </div>
                <div className="dormant-comparison">
                  <span>ACTIVITÉ DES AUTRES PHARMACIES</span>
                  <small>Clique sur une bulle verte pour préparer un transfert vers cette pharmacie.</small>
                  {pharmacies.filter((entry) => entry !== item.pharmacy).map((entry) => {
                    const other = comparison[entry];
                    const moving = Boolean(other && other.monthlySales > item.monthlySales);
                    const key = `${entry}:${item.pharmacy}:${item.ean}`;
                    const selected = Boolean(transferChoices[key]);
                    return <button
                      type="button"
                      className={`${!other ? "unknown" : moving ? "moving" : "sleeping"}${selected ? " selected" : ""}`}
                      key={entry}
                      disabled={!moving}
                      onClick={() => toggleTransfer(item, entry)}
                      title={moving ? `Préparer un transfert vers ${entry}` : undefined}
                    ><b>{selected ? "✓ " : ""}{entry}</b>{!other ? "Pas de donnée" : `${other.monthlySales.toLocaleString("fr-FR", { maximumFractionDigits: 2 })}/mois · stock ${other.stock}`}</button>;
                  })}
                </div>
                <div className="dormant-actions">
                  <button type="button" className="primary" disabled={sendingEan === item.ean} onClick={() => void sendToClearance(item)}>{sendingEan === item.ean ? "Envoi…" : "📣 Envoyer vers Produits à écouler"}</button>
                </div>
              </div>
              {open && <div className="dormant-product-details">
                <span><small>Typologie</small><strong>{item.productType}</strong></span>
                <span><small>Famille de rotation</small><strong>{item.rotationFamily || "Non classée"}</strong></span>
                <span><small>Rôle</small><strong>{item.genericRole || "Non renseigné"}</strong></span>
                <span><small>TVA</small><strong>{item.vatRate.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %</strong></span>
                <span><small>Remboursement</small><strong>{item.reimbursementRate ? `${item.reimbursementRate.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %` : "Non remboursé"}</strong></span>
                <span><small>Pharmacie</small><strong>{item.pharmacy}</strong></span>
              </div>}
            </article>;
          })}

          {!loading && !selectedItems.length && <div className="dormant-empty">
            <span>🌙</span>
            <h3>{items.length ? "Aucun produit ne correspond à ces filtres" : "Le module est prêt pour vos données"}</h3>
            <p>{items.length ? "Réduisez les seuils ou changez de pharmacie." : "Importez le premier export LGPI avec EAN, dénomination, stock, rotation, dernière vente, TVA et remboursement."}</p>
            {!items.length && <button type="button" onClick={() => setImportOpen(true)}>＋ Importer le premier catalogue</button>}
          </div>}
        </section>

        <footer className="dormant-footer">
          <div><span>🔗</span><p><strong>Catalogue commun du portail</strong><small>Cette base EAN pourra alimenter les recherches et préremplissages des autres modules.</small></p></div>
          <button type="button" onClick={onOpenClearance}>Ouvrir Produits à écouler →</button>
        </footer>
      </section>

      <section className="dormant-print-sheet" aria-hidden="true">
        <header>
          <p>SAINT-LAURENT PHARMACIES · PRODUITS DORMANTS</p>
          <h1>{selectedTransfers.length ? "Transferts à préparer" : `Listing des produits dormants · ${pharmacy}`}</h1>
          <span>{new Intl.DateTimeFormat("fr-FR", { dateStyle: "full", timeStyle: "short" }).format(new Date())}</span>
        </header>
        <table>
          <thead><tr><th>Destination</th><th>Origine</th><th>EAN</th><th>Dénomination</th><th>Stock</th><th>Dernière vente</th><th>Qté transfert</th></tr></thead>
          <tbody>{printRows.map(({ destination, item }) => <tr key={`${destination}-${item.pharmacy}-${item.ean}`}>
            <td>{destination}</td><td>{item.pharmacy}</td><td>{item.ean}</td><td>{item.product}</td><td>{item.stock}</td><td>{formatDate(item.lastSaleDate)}</td><td className="quantity">______</td>
          </tr>)}</tbody>
        </table>
        <footer>{printRows.length} ligne{printRows.length > 1 ? "s" : ""} · Signature / validation : ____________________</footer>
      </section>

      {importOpen && <div className="dormant-import-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setImportOpen(false); }}>
        <section className="dormant-import-dialog" role="dialog" aria-modal="true" aria-labelledby="dormant-import-title">
          <header><div><span>IMPORT CATALOGUE</span><h2 id="dormant-import-title">Ajouter les données de {importPharmacy}</h2><p>Copiez-collez directement les colonnes de votre export. Le portail reconnaît les séparateurs tabulation ou point-virgule.</p></div><button type="button" onClick={() => setImportOpen(false)} aria-label="Fermer">×</button></header>
          <div className="dormant-import-controls">
            <label><span>Pharmacie concernée</span><select value={importPharmacy} onChange={(event) => setImportPharmacy(event.target.value as Pharmacy)}>{pharmacies.map((entry) => <option value={entry} key={entry}>{entry}</option>)}</select></label>
            <label className="dormant-replace"><input type="checkbox" checked={replaceCatalogue} onChange={(event) => setReplaceCatalogue(event.target.checked)} /><span><strong>Remplacer le catalogue actuel</strong><small>Recommandé pour chaque nouvel export complet LGPI.</small></span></label>
          </div>
          <label className="dormant-import-paste"><span>Coller les données</span><textarea autoFocus value={importText} onChange={(event) => setImportText(event.target.value)} placeholder={sampleImport} /></label>
          <div className="dormant-import-help">
            <strong>Colonnes reconnues</strong>
            <span>EAN</span><span>Dénomination</span><span>Colonne C · nom générique</span><span>Colonne D · Prestation / PHN</span><span>Stock</span><span>Jours d’écoulement</span><span>Dernière vente</span><span>Dernière réception</span><span>TVA</span><span>Taux de remboursement</span><span>Ventes mensuelles</span>
          </div>
          <footer><button type="button" className="secondary" onClick={() => setImportOpen(false)}>Annuler</button><button type="button" className="primary" disabled={importing || !importText.trim()} onClick={() => void importCatalogue()}>{importing ? "Import en cours…" : `Importer ${parseCataloguePaste(importText).length || ""} ligne${parseCataloguePaste(importText).length > 1 ? "s" : ""}`}</button></footer>
        </section>
      </div>}

      {notice && <div className="module-toast" role="status" onClick={() => setNotice("")}>{notice}</div>}
    </main>
  );
}
